/**
 * GDPR Service
 * Frontend API client for GDPR data operations
 */

import { http } from "@/lib/http";
import { getAuthToken } from "../utils/authToken";

const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8088";

export interface UserDataExport {
  exportDate: string;
  user: {
    profile: Record<string, unknown>;
    createdAt: string;
    lastLogin?: string;
  };
  healthData: {
    fhirResources: unknown[];
    bodyData: unknown[];
    documents: unknown[];
  };
  consents: unknown[];
  metadata: {
    dataCategories: string[];
    processingPurposes: string[];
    retentionPeriod: string;
  };
}

class GDPRService {
  private getAuthHeaders() {
    const token = getAuthToken();
    return {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    };
  }

  /**
   * Export all user data (GDPR Article 20)
   */
  async exportMyData(): Promise<UserDataExport> {
    const response = await http.get(
      `${API_BASE_URL}/api/gdpr/export`,
      this.getAuthHeaders(),
    );

    return response.data.data;
  }

  /**
   * Download data export as JSON file
   */
  async downloadDataExport(): Promise<void> {
    const exportData = await this.exportMyData();

    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: "application/json",
    });

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `healthpass-data-export-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Get data processing record (GDPR Article 30)
   */
  async getProcessingRecord(): Promise<unknown> {
    const response = await http.get(
      `${API_BASE_URL}/api/gdpr/processing-record`,
      this.getAuthHeaders(),
    );

    return response.data.data;
  }

  /**
   * Get GDPR compliance info
   */
  async getGDPRInfo(): Promise<unknown> {
    const response = await http.get(`${API_BASE_URL}/api/gdpr/info`);
    return response.data.data;
  }
}

export const gdprService = new GDPRService();
